import type { Session } from '@supabase/supabase-js'
import { UserRole } from '@depanneo/types'

/**
 * Reads the app-level claims minted by the Postgres custom_access_token_hook
 * (user_id / organization_id / user_role).
 *
 * The hook injects these as TOP-LEVEL claims in the access token JWT ONLY —
 * `session.user.app_metadata` does NOT carry them (it mirrors
 * auth.users.raw_app_meta_data, which is just provider info like
 * `{ provider, providers }`). So the only place to read them is the JWT
 * payload itself.
 *
 * Decoding our own SDK-stored token locally is safe here: we never TRUST the
 * claims for authorization — the API independently verifies the JWT signature
 * on every request; this is purely client-side state hydration.
 *
 * Deliberately NOT using supabase.auth.getClaims(): on a symmetric-key (HS256)
 * project like today's prod/staging it falls back to a getUser() network
 * round-trip per call, which would reintroduce the per-navigation round-trip
 * this refactor removed. Revisit when prod migrates to asymmetric keys.
 */

export interface HookClaims {
  userId?: string
  organizationId?: string
  role: UserRole | null
}

function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  // atob yields a byte string; decode it as UTF-8 so non-ASCII claim values
  // (e.g. accented names if claims ever grow) don't mojibake.
  const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0))
  return new TextDecoder('utf-8').decode(bytes)
}

export function getHookClaims(session: Session): HookClaims {
  try {
    const payloadSegment = session.access_token.split('.')[1]
    if (!payloadSegment) return { role: null }

    const payload = JSON.parse(base64UrlDecode(payloadSegment)) as {
      user_id?: string
      organization_id?: string
      user_role?: string
    }

    return {
      userId: payload.user_id,
      organizationId: payload.organization_id,
      role: (payload.user_role as UserRole | undefined) ?? null,
    }
  } catch {
    // Malformed/non-JWT token: treat as "claims absent" rather than throwing.
    return { role: null }
  }
}
